import JSZip, { JSZipObject } from 'jszip';
import { findNodeByPath, type FSNode } from '@lved/shared-core';
import { AppwriteGitHubService } from './AppwriteGitHubService.js';
import { now, millisecondsToSeconds, normalizeRepoPath, cloneFsNode, formatSeconds } from './helpers.js';

type RepositoryArchiveSnapshotParams = {
    owner: string;
    repo: string;
    ref: string;
    privateRepository: boolean;
};

type ArchiveFileEntry = {
    path: string;
    bytes: Uint8Array;
};

type SnapshotMetrics = {
    archiveByteSize: number;
    downloadSeconds: number;
    unzipAndIndexSeconds: number;
    manifestBuildSeconds: number;
    cacheHits: number;
    cacheMisses: number;
};

export class RepositoryArchiveSnapshot {
    private readonly rootNodes: FSNode[];
    private readonly fileBytesByPath: Map<string, Uint8Array>;
    private readonly textDecoder: TextDecoder = new TextDecoder('utf-8');
    private readonly metrics: SnapshotMetrics;

    private constructor(rootNodes: FSNode[], fileBytesByPath: Map<string, Uint8Array>, metrics: SnapshotMetrics) {
        this.rootNodes = rootNodes;
        this.fileBytesByPath = fileBytesByPath;
        this.metrics = metrics;
    }

    static async load(params: RepositoryArchiveSnapshotParams): Promise<RepositoryArchiveSnapshot> {
        const downloadStartedAt: number = now();
        const archiveBytes: Uint8Array = await AppwriteGitHubService.fetchRepositoryArchive(
            params.owner,
            params.repo,
            params.ref,
            params.privateRepository
        );
        const downloadSeconds: number = millisecondsToSeconds(now() - downloadStartedAt);

        return RepositoryArchiveSnapshot.fromZipBytes(archiveBytes, downloadSeconds);
    }

    static async fromZipBytes(
        archiveBytes: Uint8Array,
        downloadSeconds: number = 0
    ): Promise<RepositoryArchiveSnapshot> {
        const unzipStartedAt: number = now();
        const zip: JSZip = await JSZip.loadAsync(archiveBytes);
        const archiveFiles: ArchiveFileEntry[] = [];
        const entries: JSZipObject[] = Object.values(zip.files);

        for (const entry of entries) {
            if (entry.dir) continue;

            const path: string | null = RepositoryArchiveSnapshot.stripArchiveRoot(entry.name);
            if (!path) continue;

            archiveFiles.push({
                path,
                bytes: await entry.async('uint8array')
            });
        }

        const unzipAndIndexSeconds: number = millisecondsToSeconds(now() - unzipStartedAt);
        const manifestStartedAt: number = now();
        const fileBytesByPath: Map<string, Uint8Array> = new Map<string, Uint8Array>();
        const rootNodes: FSNode[] = RepositoryArchiveSnapshot.buildTree(archiveFiles, fileBytesByPath);
        const manifestBuildSeconds: number = millisecondsToSeconds(now() - manifestStartedAt);

        return new RepositoryArchiveSnapshot(rootNodes, fileBytesByPath, {
            archiveByteSize: archiveBytes.byteLength,
            downloadSeconds,
            unzipAndIndexSeconds,
            manifestBuildSeconds,
            cacheHits: 0,
            cacheMisses: 0
        });
    }

    createManifest(basePath: string = ''): { path: string; children: FSNode[] } {
        const normalizedBasePath: string = normalizeRepoPath(basePath);
        if (!normalizedBasePath) {
            return {
                path: basePath,
                children: this.rootNodes.map(cloneFsNode)
            };
        }

        const targetNode: FSNode | null = findNodeByPath(this.rootNodes, normalizedBasePath);
        if (!targetNode) {
            return {
                path: basePath,
                children: []
            };
        }

        return {
            path: basePath,
            children:
                targetNode.type === 'directory' && targetNode.children
                    ? targetNode.children.map(cloneFsNode)
                    : [cloneFsNode(targetNode)]
        };
    }

    getFileData(path: string): string | null {
        const bytes: Uint8Array | undefined = this.fileBytesByPath.get(normalizeRepoPath(path));
        if (!bytes) {
            this.metrics.cacheMisses += 1;
            return null;
        }

        this.metrics.cacheHits += 1;
        return this.textDecoder.decode(bytes);
    }

    getBinaryFileData(path: string): Uint8Array | null {
        const bytes: Uint8Array | undefined = this.fileBytesByPath.get(normalizeRepoPath(path));
        if (!bytes) {
            this.metrics.cacheMisses += 1;
            return null;
        }

        this.metrics.cacheHits += 1;
        return new Uint8Array(bytes);
    }

    getMetrics(): SnapshotMetrics {
        return { ...this.metrics };
    }

    logMetrics(label: string): void {
        const metrics: SnapshotMetrics = this.getMetrics();
        console.debug(
            `[RepositoryArchiveSnapshot] ${label}. Archive download time: ${formatSeconds(metrics.downloadSeconds)} seconds. Unzip/index time: ${formatSeconds(metrics.unzipAndIndexSeconds)} seconds. Manifest build time: ${formatSeconds(metrics.manifestBuildSeconds)} seconds.`,
            {
                archiveByteSize: metrics.archiveByteSize,
                cacheHits: metrics.cacheHits,
                cacheMisses: metrics.cacheMisses
            }
        );
    }

    private static stripArchiveRoot(entryName: string): string | null {
        const parts: string[] = entryName.split('/').filter(Boolean);
        if (parts.length <= 1) return null;

        const repoRelativePath: string = parts.slice(1).join('/');
        return repoRelativePath && !repoRelativePath.startsWith('__MACOSX/') ? repoRelativePath : null;
    }

    private static buildTree(archiveFiles: ArchiveFileEntry[], fileBytesByPath: Map<string, Uint8Array>): FSNode[] {
        const rootNodes: FSNode[] = [];
        const directoryNodesByPath: Map<string, FSNode> = new Map<string, FSNode>();

        // Directory entries are not required in ZIP archives, so create missing parent
        // directories on demand and deduplicate them by their repo-relative path.
        const ensureDirectory = (directoryPath: string): FSNode => {
            const existingNode: FSNode | undefined = directoryNodesByPath.get(directoryPath);
            if (existingNode) return existingNode;

            const pathParts: string[] = directoryPath.split('/').filter(Boolean);
            const name: string = pathParts[pathParts.length - 1];
            const node: FSNode = {
                name,
                path: directoryPath,
                type: 'directory',
                children: []
            };
            directoryNodesByPath.set(directoryPath, node);

            if (pathParts.length === 1) {
                rootNodes.push(node);
                return node;
            }

            const parentPath: string = pathParts.slice(0, -1).join('/');
            const parentNode: FSNode = ensureDirectory(parentPath);
            parentNode.children?.push(node);
            return node;
        };

        // Stable path order keeps the generated manifest deterministic for tests and UI refreshes.
        const sortedFiles: ArchiveFileEntry[] = [...archiveFiles].sort((a: ArchiveFileEntry, b: ArchiveFileEntry) =>
            a.path.localeCompare(b.path)
        );

        for (const file of sortedFiles) {
            const normalizedPath: string = normalizeRepoPath(file.path);
            const pathParts: string[] = normalizedPath.split('/').filter(Boolean);
            if (pathParts.length === 0) continue;

            // Store bytes under the same normalized path used by getFileData/getBinaryFileData.
            fileBytesByPath.set(normalizedPath, file.bytes);

            const fileNode: FSNode = {
                name: pathParts[pathParts.length - 1],
                path: normalizedPath,
                type: 'file',
                size: file.bytes.byteLength
            };

            if (pathParts.length === 1) {
                rootNodes.push(fileNode);
                continue;
            }

            // Attach nested files to a guaranteed parent chain, creating directories if
            // the archive did not include explicit directory records.
            const parentPath: string = pathParts.slice(0, -1).join('/');
            const parentNode: FSNode = ensureDirectory(parentPath);
            parentNode.children?.push(fileNode);
        }

        return rootNodes;
    }
}
